import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { rfiSectionClient, useRfiSections } from '../rfiSectionHooks';
/** Answer sections in position order: live, top-level, `role='answer'` (WRT-05). */ export function answerSections(sections) {
    return (sections ?? []).filter((s)=>!s.parentSectionId && (s.role === '' || s.role === 'answer')).sort((a, b)=>a.position - b.position);
}
export function useRfiAnswerSections(rfiResponseProjectId) {
    const query = useRfiSections(rfiResponseProjectId);
    const sections = useMemo(()=>answerSections(query.data), [
        query.data
    ]);
    return {
        ...query,
        sections
    };
}
export const RESPONSE_DOCUMENT_KEY = 'rfi-response-document';
/**
 * `EnsureRfiResponseDocument`, once per RFI per session (query cache, never
 * stale). `templateAttachmentId`: '' = blank or the only .docx; 'blank'; or an
 * attachment the user picked in the template dialog.
 */ export function useEnsureRfiResponseDocument(rfiResponseProjectId, enabled, templateAttachmentId = '') {
    return useQuery({
        queryKey: [
            RESPONSE_DOCUMENT_KEY,
            rfiResponseProjectId,
            templateAttachmentId
        ],
        enabled: enabled && Boolean(rfiResponseProjectId),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
        retry: false,
        refetchOnWindowFocus: false,
        queryFn: ()=>rfiSectionClient.ensureRfiResponseDocument({
                rfiResponseProjectId,
                templateAttachmentId
            })
    });
}
/** `EnsureRfiAnswerSections`: seeds new questions and re-inserts missing headings. */ export function useEnsureRfiAnswerSections(rfiResponseProjectId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ()=>rfiSectionClient.ensureRfiAnswerSections({
                rfiResponseProjectId
            }),
        onSuccess: ()=>Promise.all([
                qc.invalidateQueries({
                    queryKey: [
                        'rfi-sections',
                        rfiResponseProjectId
                    ]
                }),
                qc.invalidateQueries({
                    queryKey: [
                        'officeWorkspace'
                    ]
                })
            ])
    });
}
export function useRfiNavCounts(rfiResponseProjectId) {
    return useQuery({
        queryKey: [
            'rfi-nav-counts',
            rfiResponseProjectId
        ],
        enabled: Boolean(rfiResponseProjectId),
        staleTime: 30000,
        queryFn: ()=>rfiSectionClient.getRfiNavCounts({
                rfiResponseProjectId
            })
    });
}
/**
 * Maps `GetRfiNavCounts` to the §4.2 sidebar count keys. Zero counts that
 * WRT-06/07 have not filled yet stay hidden (undefined).
 */ export function rfiNavCountsToKeys(c) {
    if (!c) return {};
    const some = (n)=>n > 0 ? n : undefined;
    return {
        questions: c.questions,
        reviews: some(c.inReview),
        notice_files: some(c.attachments),
        labor_categories: some(c.laborCategories),
        amendments: some(c.amendments),
        past_answers: some(c.pastAnswers),
        team: some(c.team)
    };
}
